import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { BottomSheet } from "./BottomSheet";
import { errorText, haptic, isGone, packApi } from "../lib/packApi";

/** Rename a pack. */
export function RenamePackSheet({
  open,
  name,
  title,
  onClose,
  onDone,
  onGone,
}: {
  open: boolean;
  name: string;
  title: string;
  onClose: () => void;
  onDone: (newTitle: string) => void;
  /** the pack was already deleted on Telegram: caller should drop it from the screen */
  onGone?: () => void;
}) {
  const [value, setValue] = useState(title);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(title);
      setError(null);
    }
  }, [open, title]);

  const save = async () => {
    const next = value.trim();
    if (!next || busy) return;
    if (next === title) return onClose();
    setBusy(true);
    setError(null);
    try {
      const res = await packApi.rename(name, next);
      haptic("light");
      onDone(res.title ?? next);
      onClose();
    } catch (err) {
      if (isGone(err)) {
        onGone?.();
        onClose();
        return;
      }
      setError(errorText(err, "تعذر تغيير الاسم"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} locked={busy}>
      <h3 className="font-semibold text-base mb-3">اسم الحزمة</h3>
      <input
        type="text"
        value={value}
        maxLength={64}
        dir="rtl"
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="اسم الحزمة"
        className="w-full rounded-xl glass-pill px-4 py-3 text-base outline-none placeholder:text-tg-hint"
      />
      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      <div className="flex gap-3 mt-4">
        <button type="button" onClick={onClose} disabled={busy} className="flex-1 glass-pill py-3 rounded-xl text-tg-text">
          إلغاء
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy || !value.trim()}
          className="flex-1 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          حفظ
        </button>
      </div>
    </BottomSheet>
  );
}

/** Confirm + delete a whole pack. */
export function DeletePackSheet({
  open,
  name,
  title,
  onClose,
  onDone,
}: {
  open: boolean;
  name: string;
  title: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await packApi.remove(name);
      haptic("medium");
      onDone();
      onClose();
    } catch (err) {
      if (isGone(err)) {
        // already deleted elsewhere: that is exactly what the user wanted
        onDone();
        onClose();
        return;
      }
      setError(errorText(err, "تعذر حذف الحزمة"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} locked={busy}>
      <div className="flex flex-col items-center text-center gap-3 pt-1">
        <div className="w-12 h-12 rounded-full glass-pill flex items-center justify-center">
          <AlertTriangle className="text-red-400" size={22} strokeWidth={2.25} />
        </div>
        <h3 className="font-semibold text-base">حذف «{title}»؟</h3>
        <p className="text-tg-hint text-sm max-w-xs">
          ستُحذف الحزمة وكل ملصقاتها نهائياً من تيليجرام، ولا يمكن التراجع عن ذلك.
        </p>
      </div>
      {error && <p className="text-red-400 text-sm mt-3 text-center">{error}</p>}
      <div className="flex gap-3 mt-5">
        <button type="button" onClick={onClose} disabled={busy} className="flex-1 glass-pill py-3 rounded-xl text-tg-text">
          إلغاء
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: "#c0392b", color: "#fff" }}
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          حذف نهائياً
        </button>
      </div>
    </BottomSheet>
  );
}
